import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageHero } from "@/components/site/PageParts";
import { Reveal } from "@/lib/motion";
import { MapPin, Phone, EnvelopeSimple, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { CONTACT } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete every field.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 30000 });
      toast.success("Message sent — we'll be in touch soon.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const fieldCls =
    "w-full bg-transparent border-b border-line py-4 text-lg placeholder:text-ink-soft/60 focus:outline-none focus:border-oxblood transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHero
        index="F/"
        overline="Contact"
        titleLines={["Get in", <>touch <span className="italic text-oxblood">discreetly</span></>]}
        intro="Questions, feedback, or a concern to report? We read every message."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-oxblood mb-8">
              Interlaced Social LLC
            </p>
            <div className="space-y-8">
              <div className="flex gap-4">
                <MapPin size={24} weight="light" className="text-oxblood shrink-0 mt-1" />
                <div>
                  <p className="text-sm uppercase tracking-wider text-ink-soft mb-1">Address</p>
                  <p className="text-lg">{CONTACT.address}</p>
                </div>
              </div>
              <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex gap-4 group">
                <Phone size={24} weight="light" className="text-oxblood shrink-0 mt-1" />
                <div>
                  <p className="text-sm uppercase tracking-wider text-ink-soft mb-1">Phone</p>
                  <p className="text-lg group-hover:text-oxblood transition-colors">{CONTACT.phone}</p>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex gap-4 group">
                <EnvelopeSimple size={24} weight="light" className="text-oxblood shrink-0 mt-1" />
                <div>
                  <p className="text-sm uppercase tracking-wider text-ink-soft mb-1">Email</p>
                  <p className="text-lg group-hover:text-oxblood transition-colors break-all">{CONTACT.email}</p>
                </div>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input
                  data-testid="contact-name"
                  className={fieldCls}
                  placeholder="Your name"
                  value={form.name}
                  onChange={update("name")}
                  aria-label="Your name"
                />
                <input
                  data-testid="contact-email-input"
                  type="email"
                  className={fieldCls}
                  placeholder="Email address"
                  value={form.email}
                  onChange={update("email")}
                  aria-label="Email address"
                />
              </div>
              <input
                data-testid="contact-subject"
                className={fieldCls}
                placeholder="Subject"
                value={form.subject}
                onChange={update("subject")}
                aria-label="Subject"
              />
              <textarea
                data-testid="contact-message"
                className={`${fieldCls} resize-none`}
                placeholder="Your message"
                rows={5}
                value={form.message}
                onChange={update("message")}
                aria-label="Your message"
              />
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={sending}
                className="group relative inline-flex items-center gap-3 overflow-hidden bg-ink text-paper px-10 py-5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60"
              >
                <span className="relative z-10 flex items-center gap-3">
                  {sending ? (
                    <>
                      <CircleNotch size={16} className="animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send message
                      <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
                    </>
                  )}
                </span>
                <span className="absolute inset-0 bg-oxblood translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
